/**
 * Silences the benign "ResizeObserver loop completed with undelivered
 * notifications" warning that Recharts / responsive layouts emit and that CRA's
 * dev overlay escalates into a fatal-looking error.
 *
 * IMPORTANT: this module must be imported *first* in the app entry (before React,
 * Recharts, or anything that reads `window.ResizeObserver`). ES imports evaluate
 * in order, so importing this first guarantees the patched observer is installed
 * before any library caches the native constructor.
 */
function isResizeObserverLoopError(message) {
  if (!message) return false;
  const msg = String(message);
  return (
    msg === 'ResizeObserver loop completed with undelivered notifications.' ||
    msg === 'ResizeObserver loop limit exceeded' ||
    msg.includes('ResizeObserver loop')
  );
}

if (typeof window !== 'undefined') {
  // Defer the observer callback to a macrotask (setTimeout) so it runs *outside*
  // the browser's resize-observation cycle. A requestAnimationFrame callback can
  // still execute within the same layout pass in Chrome, which is why the "loop
  // completed with undelivered notifications" warning survived the RAF approach.
  // A tiny debounce also coalesces the burst of notifications Recharts produces
  // while a chart settles, keeping only the latest entries.
  const NativeResizeObserver = window.ResizeObserver;
  if (NativeResizeObserver && !window.__edgefinROPatched) {
    const Patched = class ResizeObserver extends NativeResizeObserver {
      constructor(callback) {
        let timer = 0;
        super((entries, observer) => {
          if (timer) window.clearTimeout(timer);
          timer = window.setTimeout(() => {
            timer = 0;
            try {
              callback(entries, observer);
            } catch (err) {
              if (!isResizeObserverLoopError(err && err.message)) throw err;
            }
          }, 20);
        });
      }
    };
    window.ResizeObserver = Patched;
    window.__edgefinROPatched = true;
  }

  const previousOnError = window.onerror;
  window.onerror = function resizeObserverOnError(message, source, lineno, colno, error) {
    if (isResizeObserverLoopError(message) || isResizeObserverLoopError(error && error.message)) {
      return true;
    }
    return previousOnError ? previousOnError(message, source, lineno, colno, error) : false;
  };

  // Capture phase + stopImmediatePropagation preempts the CRA / webpack-dev-server
  // error overlay listeners so the popup never appears for this benign warning.
  window.addEventListener(
    'error',
    (event) => {
      if (isResizeObserverLoopError(event.message) || isResizeObserverLoopError(event.error && event.error.message)) {
        event.stopImmediatePropagation();
        event.preventDefault();
      }
    },
    true
  );

  const nativeConsoleError = console.error.bind(console);
  console.error = (...args) => {
    const first = args[0];
    if (typeof first === 'string' && isResizeObserverLoopError(first)) return;
    if (first instanceof Error && isResizeObserverLoopError(first.message)) return;
    nativeConsoleError(...args);
  };
}

export {};
